/**
 * My Challenges Section
 * Displays player's pending ladder challenges and allows self-reporting results
 */

import { useState } from 'react';
import { doc, updateDoc, Timestamp, writeBatch } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { LadderEntry, LadderSettings } from '../types';

export interface Challenge {
    id: string;
    challengerId: string;
    challengerName: string;
    challengerRankAtTime: number;
    defenderId: string;
    defenderName: string;
    defenderRankAtTime: number;
    status: string;
    isPreseasonMatch: boolean;
    challengerReport?: {
        claimedWinner: 'challenger' | 'defender';
        score?: string;
        reportedAt: Timestamp;
    };
    defenderReport?: {
        claimedWinner: 'challenger' | 'defender';
        score?: string;
        reportedAt: Timestamp;
    };
    createdAt: Timestamp;
}

interface MyChallengesSectionProps {
    playerId: string;
    challenges: Challenge[];
    rankings: LadderEntry[];
    settings: LadderSettings | null;
    onRefresh: () => void;
}

export default function MyChallengesSection({
    playerId,
    challenges,
    rankings,
    settings: _settings,
    onRefresh,
}: MyChallengesSectionProps) {
    const [reporting, setReporting] = useState<string | null>(null);
    const [selectedWinner, setSelectedWinner] = useState<'challenger' | 'defender' | null>(null);
    const [score, setScore] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [processing, setProcessing] = useState<string | null>(null);

    // Accept a challenge (defender only, preseason)
    const handleAcceptChallenge = async (challengeId: string) => {
        setProcessing(challengeId);
        try {
            await updateDoc(doc(db, 'ssbuChallenges', challengeId), {
                status: 'accepted',
                acceptedAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            });
            onRefresh();
        } catch (error) {
            console.error('Error accepting challenge:', error);
        } finally {
            setProcessing(null);
        }
    };

    // Decline a challenge (defender only, preseason)
    const handleDeclineChallenge = async (challengeId: string) => {
        setProcessing(challengeId);
        try {
            await updateDoc(doc(db, 'ssbuChallenges', challengeId), {
                status: 'declined',
                declinedAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            });
            onRefresh();
        } catch (error) {
            console.error('Error declining challenge:', error);
        } finally {
            setProcessing(null);
        }
    };

    const myChallenges = challenges.filter(
        c => (c.challengerId === playerId || c.defenderId === playerId) &&
            ['pending', 'accepted', 'awaiting_confirm', 'scheduled'].includes(c.status)
    );

    if (myChallenges.length === 0) {
        return null; // Don't show section if no challenges
    }

    const handleReportResult = async (challenge: Challenge) => {
        if (!selectedWinner) return;

        const isChallenger = challenge.challengerId === playerId;
        setSubmitting(true);

        try {
            const reportField = isChallenger ? 'challengerReport' : 'defenderReport';
            const report = {
                claimedWinner: selectedWinner,
                score: score || undefined,
                reportedAt: Timestamp.now(),
            };

            // Check if other player already reported
            const otherReport = isChallenger ? challenge.defenderReport : challenge.challengerReport;
            let newStatus = 'awaiting_confirm';

            if (otherReport) {
                // Both have now reported - check if they agree
                if (otherReport.claimedWinner === selectedWinner) {
                    // They agree! Complete the challenge
                    newStatus = 'completed';

                    // If challenger won, swap ranks
                    if (selectedWinner === 'challenger') {
                        const challengerEntry = rankings.find(r => r.playerRosterId === challenge.challengerId);
                        const defenderEntry = rankings.find(r => r.playerRosterId === challenge.defenderId);

                        if (challengerEntry && defenderEntry) {
                            const batch = writeBatch(db);

                            // Update challenger rank and stats
                            batch.update(doc(db, 'ssbuLadder', challengerEntry.id), {
                                rank: defenderEntry.rank,
                                previousRank: challengerEntry.rank,
                                'stats.challengesWon': challengerEntry.stats.challengesWon + 1,
                                'stats.currentStreak': challengerEntry.stats.currentStreak > 0
                                    ? challengerEntry.stats.currentStreak + 1
                                    : 1,
                                'stats.bestRank': Math.min(challengerEntry.stats.bestRank, defenderEntry.rank),
                                updatedAt: Timestamp.now()
                            });

                            // Shift down all players between the two ranks
                            const playersToShift = rankings.filter(
                                r => r.rank >= defenderEntry.rank && r.rank < challengerEntry.rank && r.id !== challengerEntry.id
                            );

                            playersToShift.forEach(p => {
                                batch.update(doc(db, 'ssbuLadder', p.id), {
                                    rank: p.rank + 1,
                                    previousRank: p.rank,
                                    updatedAt: Timestamp.now()
                                });
                            });

                            // Update defender stats
                            batch.update(doc(db, 'ssbuLadder', defenderEntry.id), {
                                'stats.defensesPlayed': defenderEntry.stats.defensesPlayed + 1,
                                'stats.currentStreak': defenderEntry.stats.currentStreak < 0
                                    ? defenderEntry.stats.currentStreak - 1
                                    : -1,
                                updatedAt: Timestamp.now()
                            });

                            await batch.commit();
                        }
                    } else {
                        // Defender won - update stats only
                        const challengerEntry = rankings.find(r => r.playerRosterId === challenge.challengerId);
                        const defenderEntry = rankings.find(r => r.playerRosterId === challenge.defenderId);

                        if (challengerEntry && defenderEntry) {
                            const batch = writeBatch(db);

                            batch.update(doc(db, 'ssbuLadder', challengerEntry.id), {
                                'stats.currentStreak': challengerEntry.stats.currentStreak < 0
                                    ? challengerEntry.stats.currentStreak - 1
                                    : -1,
                                updatedAt: Timestamp.now()
                            });

                            batch.update(doc(db, 'ssbuLadder', defenderEntry.id), {
                                'stats.defensesPlayed': defenderEntry.stats.defensesPlayed + 1,
                                'stats.defensesWon': defenderEntry.stats.defensesWon + 1,
                                'stats.currentStreak': defenderEntry.stats.currentStreak > 0
                                    ? defenderEntry.stats.currentStreak + 1
                                    : 1,
                                updatedAt: Timestamp.now()
                            });

                            await batch.commit();
                        }
                    }
                } else {
                    // Disagreement - mark as disputed
                    newStatus = 'disputed';
                }
            }

            // Update challenge document
            await updateDoc(doc(db, 'ssbuChallenges', challenge.id), {
                [reportField]: report,
                status: newStatus,
                ...(newStatus === 'completed' ? {
                    completedDate: Timestamp.now(),
                    result: { winner: selectedWinner, score: score || undefined }
                } : {}),
                updatedAt: Timestamp.now()
            });

            setReporting(null);
            setSelectedWinner(null);
            setScore('');
            onRefresh();
        } catch (error) {
            console.error('Error reporting result:', error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <span>🎯</span> My Challenges
                <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                    {myChallenges.length} active
                </span>
            </h2>

            <div className="space-y-3">
                {myChallenges.map(challenge => {
                    const isChallenger = challenge.challengerId === playerId;
                    const myReport = isChallenger ? challenge.challengerReport : challenge.defenderReport;
                    const theirReport = isChallenger ? challenge.defenderReport : challenge.challengerReport;
                    const opponentName = isChallenger ? challenge.defenderName : challenge.challengerName;

                    return (
                        <div key={challenge.id} className="card p-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${challenge.status === 'awaiting_confirm'
                                        ? 'bg-yellow-500/20 text-yellow-400'
                                        : challenge.status === 'disputed'
                                            ? 'bg-red-500/20 text-red-400'
                                            : challenge.status === 'accepted'
                                                ? 'bg-green-500/20 text-green-400'
                                                : challenge.status === 'pending'
                                                    ? 'bg-orange-500/20 text-orange-400'
                                                    : 'bg-secondary-700 text-secondary-300'
                                        }`}>
                                        {challenge.status === 'awaiting_confirm'
                                            ? 'Awaiting Confirmation'
                                            : challenge.status === 'pending'
                                                ? 'Pending Acceptance'
                                                : challenge.status === 'accepted'
                                                    ? 'Accepted - Play Match'
                                                    : challenge.status.toUpperCase().replace('_', ' ')}
                                    </span>
                                    {challenge.isPreseasonMatch && (
                                        <span className="text-xs text-yellow-400">⚡ Preseason</span>
                                    )}
                                </div>
                                <span className="text-xs text-secondary-500">
                                    {isChallenger ? 'You challenged' : 'Challenged by'}
                                </span>
                            </div>

                            <div className="flex items-center gap-4 mb-4">
                                <div className="flex-1 text-center">
                                    <div className="text-sm text-secondary-400">
                                        {isChallenger ? 'You' : 'Challenger'}
                                    </div>
                                    <div className={`font-bold ${isChallenger ? 'text-primary-400' : 'text-white'}`}>
                                        {challenge.challengerName}
                                    </div>
                                    <div className="text-secondary-500 text-sm">
                                        Rank #{challenge.challengerRankAtTime}
                                    </div>
                                </div>
                                <div className="text-2xl text-secondary-500">⚔️</div>
                                <div className="flex-1 text-center">
                                    <div className="text-sm text-secondary-400">
                                        {!isChallenger ? 'You' : 'Defender'}
                                    </div>
                                    <div className={`font-bold ${!isChallenger ? 'text-primary-400' : 'text-white'}`}>
                                        {challenge.defenderName}
                                    </div>
                                    <div className="text-secondary-500 text-sm">
                                        Rank #{challenge.defenderRankAtTime}
                                    </div>
                                </div>
                            </div>

                            {/* Report Status */}
                            {(myReport || theirReport) && (
                                <div className="mb-4 p-3 bg-secondary-800 rounded-lg text-sm">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <span className="text-secondary-400">Your report: </span>
                                            {myReport ? (
                                                <span className="text-white">
                                                    {myReport.claimedWinner === (isChallenger ? 'challenger' : 'defender') ? 'I won' : 'I lost'}
                                                    {myReport.score && ` (${myReport.score})`}
                                                </span>
                                            ) : (
                                                <span className="text-yellow-400">Not reported</span>
                                            )}
                                        </div>
                                        <div>
                                            <span className="text-secondary-400">{opponentName}: </span>
                                            {theirReport ? (
                                                <span className="text-white">
                                                    {theirReport.claimedWinner === (isChallenger ? 'defender' : 'challenger') ? 'They won' : 'They lost'}
                                                    {theirReport.score && ` (${theirReport.score})`}
                                                </span>
                                            ) : (
                                                <span className="text-secondary-500">Waiting...</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Accept/Decline for Defender (pending challenges) */}
                            {!isChallenger && challenge.status === 'pending' && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleAcceptChallenge(challenge.id)}
                                        disabled={processing === challenge.id}
                                        className="btn-primary text-sm flex-1 disabled:opacity-50"
                                    >
                                        {processing === challenge.id ? '...' : '✅ Accept Challenge'}
                                    </button>
                                    <button
                                        onClick={() => handleDeclineChallenge(challenge.id)}
                                        disabled={processing === challenge.id}
                                        className="btn-secondary text-sm flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-50"
                                    >
                                        ❌ Decline
                                    </button>
                                </div>
                            )}

                            {/* Waiting for opponent to accept (Challenger view) */}
                            {isChallenger && challenge.status === 'pending' && (
                                <p className="text-sm text-yellow-400 text-center">
                                    ⏳ Waiting for {opponentName} to accept your challenge
                                </p>
                            )}

                            {/* Report Result Form (only for accepted challenges) */}
                            {!myReport && ['accepted', 'scheduled', 'awaiting_confirm'].includes(challenge.status) && (
                                <>
                                    {reporting === challenge.id ? (
                                        <div className="space-y-3">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => setSelectedWinner(isChallenger ? 'challenger' : 'defender')}
                                                    className={`flex-1 p-2 rounded-lg text-sm font-medium transition-colors ${selectedWinner === (isChallenger ? 'challenger' : 'defender')
                                                        ? 'bg-green-500/20 text-green-400 border border-green-500'
                                                        : 'bg-secondary-700 text-secondary-300 hover:bg-secondary-600'
                                                        }`}
                                                >
                                                    ✅ I Won
                                                </button>
                                                <button
                                                    onClick={() => setSelectedWinner(isChallenger ? 'defender' : 'challenger')}
                                                    className={`flex-1 p-2 rounded-lg text-sm font-medium transition-colors ${selectedWinner === (isChallenger ? 'defender' : 'challenger')
                                                        ? 'bg-red-500/20 text-red-400 border border-red-500'
                                                        : 'bg-secondary-700 text-secondary-300 hover:bg-secondary-600'
                                                        }`}
                                                >
                                                    ❌ I Lost
                                                </button>
                                            </div>
                                            <input
                                                type="text"
                                                placeholder="Score (optional, e.g. 2-1)"
                                                value={score}
                                                onChange={(e) => setScore(e.target.value)}
                                                className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                                            />
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => { setReporting(null); setSelectedWinner(null); setScore(''); }}
                                                    className="btn-secondary text-sm"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={() => handleReportResult(challenge)}
                                                    disabled={!selectedWinner || submitting}
                                                    className="btn-primary text-sm flex-1 disabled:opacity-50"
                                                >
                                                    {submitting ? 'Submitting...' : 'Submit Result'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => setReporting(challenge.id)}
                                            className="btn-primary text-sm w-full"
                                        >
                                            📝 Report Match Result
                                        </button>
                                    )}
                                </>
                            )}

                            {myReport && !theirReport && (
                                <p className="text-sm text-yellow-400 text-center">
                                    ⏳ Waiting for {opponentName} to confirm the result
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
